import cn from 'classnames';
import {ButtonHTMLAttributes, Children, cloneElement, FC, isValidElement, PropsWithChildren, ReactNode} from 'react';
import styles from './StepLayout.module.css';

export type StepLayoutIllustrationSize = 'default' | 'big';

type RootProps = PropsWithChildren<{
	variant?: 'default' | 'fill' | 'compact';
	className?: string;
}>;

type ContentProps = PropsWithChildren<{
	className?: string;
}>;

type IntroProps = {
	title: ReactNode;
	subtitle?: ReactNode;
	compact?: boolean;
	subtitleMuted?: boolean;
};

type IllustrationProps = PropsWithChildren<{
	className?: string;
	size?: StepLayoutIllustrationSize;
}>;

type TextLinkProps = ButtonHTMLAttributes<HTMLButtonElement>;

interface StepLayoutComposition {
	Title: typeof Title;
	Subtitle: typeof Subtitle;
	Intro: typeof Intro;
	Content: typeof Content;
	Illustration: typeof Illustration;
	TextLink: typeof TextLink;
}

const Root: FC<RootProps> = ({variant = 'default', className, children}) => (
	<section
		className={cn(
			styles.root,
			variant === 'fill' && styles.fill,
			variant === 'compact' && styles.rootCompact,
			className,
		)}
	>
		{children}
	</section>
);

const Title: FC<PropsWithChildren> = ({children}) => <h1 className={styles.title}>{children}</h1>;

const Subtitle: FC<PropsWithChildren<{muted?: boolean}>> = ({children, muted}) => (
	<p className={muted ? styles.subtitleMuted : styles.subtitle}>{children}</p>
);

const Intro: FC<IntroProps> = ({title, subtitle, compact, subtitleMuted = false}) => (
	<div className={compact ? styles.introCompact : styles.intro}>
		<Title>{title}</Title>
		{subtitle ? <Subtitle muted={subtitleMuted}>{subtitle}</Subtitle> : null}
	</div>
);

const Content: FC<ContentProps> = ({className, children}) => (
	<div className={cn(styles.content, className)}>{children}</div>
);

const Illustration: FC<IllustrationProps> = ({className, size = 'default', children}) => (
	<div className={cn(styles.illustration, className)}>
		{Children.map(children, (child) => {
			if (!isValidElement<{className?: string}>(child)) {
				return child;
			}

			return cloneElement(child, {
				className: cn(
					styles.illustrationIcon,
					size === 'big' && styles.illustrationIconBig,
					child.props.className,
				),
			});
		})}
	</div>
);

const TextLink: FC<TextLinkProps> = ({className, type = 'button', ...props}) => (
	<button type={type} className={cn(styles.textLink, className)} {...props} />
);

export const StepLayout: FC<RootProps> & StepLayoutComposition = Object.assign(Root, {
	Title,
	Subtitle,
	Intro,
	Content,
	Illustration,
	TextLink,
});

